import React, { useState, useEffect } from "react";
import { Button, Box } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import AuthPageLayout from "../../../components/layout/AuthPageLayout";
import VerificationCodeInput from "../../../components/VerificationCodeInput";
import { FormErrorContainer } from "../../../components/FormErrorContainer";
import { useApiErrorHandling } from "../../../hooks";
import Checkbox from "../../../components/Checkbox";
import {
  twoFactorAuth,
  verifyRegistrationPhoneCode,
  resendLoginTwoFactorCode,
  sendRegistrationPhoneCode,
} from "../../../store/actions/authActions";
import { ROUTES, STORAGE_KEYS, COOKIE_KEYS } from "../../../constants";
import { getCookie } from "../../../utils/cookieUtils";
import {
  appendExternalIdQuery,
  getPostLoginRoute,
  EXTERNAL_ID_QUERY_PARAM,
} from "../../../utils/routeUtils";
import {
  VerificationContainer,
  VerificationTitle,
  VerificationSubtitle,
  VerificationForm,
  VerificationActions,
  VerificationBackupLink,
} from "./VerificationPage.styled";

const VerificationPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [countdown, setCountdown] = useState(60);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { apiErrors, clearErrors, handleApiError } = useApiErrorHandling({
    defaultErrorMessage: "Verification failed. Please try again.",
  });

  // Check if this is registration flow
  const isRegistrationFlow = localStorage.getItem(
    STORAGE_KEYS.REGISTRATION_TOKEN,
  );

  // Check if user came from backup code page (no redirect parameter)
  const isFromBackupCode = !searchParams.get("redirect");
  const externalId = searchParams.get(EXTERNAL_ID_QUERY_PARAM);

  // Start countdown timer when page loads (except when coming from backup code)
  useEffect(() => {
    if (isFromBackupCode) {
      // If coming from backup code, enable resend button immediately
      setIsResendDisabled(false);
      setCountdown(0);
    } else {
      // Start countdown timer immediately when page loads
      setCountdown(60);
      setIsResendDisabled(true);
    }
  }, [isFromBackupCode]);

  // Countdown timer for resend
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else {
      setIsResendDisabled(false);
    }

    return () => clearTimeout(timer);
  }, [countdown]);

  const handleCodeChange = (newCode: string) => {
    setCode(newCode);
  };

  const handleCodeComplete = (_completedCode: string) => {
    // Don't auto-submit, let user click Verify button
  };

  const handleSubmit = async (verificationCode?: string) => {
    const codeToSubmit = verificationCode || code;
    if (codeToSubmit.length !== 6) return;

    clearErrors();
    setIsSubmitting(true);
    try {
      if (isRegistrationFlow) {
        // Registration flow
        await dispatch(verifyRegistrationPhoneCode(codeToSubmit)).unwrap();
        navigate(ROUTES.SAVE_BACKUP_CODES);
      } else {
        // Login flow
        const email = getCookie(COOKIE_KEYS.EMAIL_HINT);
        if (!email) {
          handleApiError("Email not found. Please try logging in again.");
          return;
        }
        const result = await dispatch(
          twoFactorAuth({ email, code: codeToSubmit, rememberDevice }),
        ).unwrap();
        navigate(getPostLoginRoute(result.user?.roleId, externalId));
      }
    } catch (error: unknown) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    clearErrors();
    try {
      if (isRegistrationFlow) {
        // For registration flow
        const phoneNumber = getCookie(COOKIE_KEYS.PHONE_HINT);
        if (!phoneNumber) {
          handleApiError(
            "Phone number not found. Please try again from the previous step.",
          );
          return;
        }
        await dispatch(sendRegistrationPhoneCode(phoneNumber)).unwrap();
      } else {
        // For login flow - use resend endpoint
        const email = getCookie(COOKIE_KEYS.EMAIL_HINT);
        if (!email) {
          handleApiError(
            "Email not found. Please try again from the previous step.",
          );
          return;
        }
        await dispatch(resendLoginTwoFactorCode(email)).unwrap();
      }

      // Reset countdown
      setCountdown(60);
      setIsResendDisabled(true);
      clearErrors();
    } catch (error: unknown) {
      handleApiError(error);
    }
  };

  return (
    <AuthPageLayout contentWidth="694px">
      <VerificationContainer>
        <VerificationTitle>{t("verification.title")}</VerificationTitle>

        <VerificationSubtitle>
          {t("verification.subtitle")}
        </VerificationSubtitle>

        <VerificationForm>
          <VerificationCodeInput
            length={6}
            onComplete={handleCodeComplete}
            onChange={handleCodeChange}
            error={false}
          />

          <FormErrorContainer errors={apiErrors} />

          <Box sx={{ textAlign: "center" }}>
            {t("verification.didntGetCode")}{" "}
            {isResendDisabled ? (
              <Box component="span" sx={{ color: "#666" }}>
                {t("verification.resendIn", { seconds: countdown })}
              </Box>
            ) : (
              <Button
                variant="text"
                onClick={handleResend}
                disabled={isSubmitting}
                sx={{
                  minWidth: "auto",
                  p: 0,
                  textTransform: "none",
                  fontSize: "14px",
                  color: "primary.main",
                }}
              >
                {t("verification.resendCode")}
              </Button>
            )}
          </Box>

          {!isRegistrationFlow && (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Checkbox
                checked={rememberDevice}
                onChange={setRememberDevice}
                label={t("verification.rememberDevice")}
              />
            </Box>
          )}

          <VerificationActions>
            <Button
              variant="outlined"
              onClick={handleResend}
              disabled={isResendDisabled || isSubmitting}
            >
              {t("verification.resendCode")}
            </Button>

            <Button
              variant="contained"
              onClick={() => handleSubmit()}
              disabled={code.length !== 6 || isSubmitting}
            >
              {t("verification.verify")}
            </Button>
          </VerificationActions>

          {!isRegistrationFlow && (
            <VerificationBackupLink>
              <Button
                variant="text"
                onClick={() =>
                  navigate(appendExternalIdQuery(ROUTES.BACKUP_CODE, externalId))
                }
              >
                {t("verification.useBackupCode")}
              </Button>
            </VerificationBackupLink>
          )}
        </VerificationForm>
      </VerificationContainer>
    </AuthPageLayout>
  );
};

export default VerificationPage;
